/**
 * Panel - Main accessibility panel UI
 * Handles floating button, slide-in panel, feature cards grid
 */
export default class Panel {
    constructor(widget) {
        this.widget = widget;
        this.isOpen = false;
        this.container = null;
        this.button = null;
        this.panelEl = null;

        this._create();
        this._bindEvents();
        this._checkHiddenState();
    }

    /**
     * Create the widget DOM structure
     */
    _create() {
        // Root container
        this.container = document.createElement('div');
        this.container.id = 'accessibility-widget';
        this.container.setAttribute('role', 'complementary');
        this.container.setAttribute('aria-label', this.widget.i18n.t('widget.title'));

        // Floating button
        this.button = this._createFloatingButton();
        this.container.appendChild(this.button);

        // Panel
        this.panelEl = this._createPanel();
        this.container.appendChild(this.panelEl);

        // Hide widget modal
        this.hideModal = this._createHideModal();
        this.container.appendChild(this.hideModal);

        // Restore button (shown when widget is hidden)
        this.restoreBtn = this._createRestoreButton();
        this.container.appendChild(this.restoreBtn);

        document.body.appendChild(this.container);
    }

    /**
     * Create floating accessibility button
     */
    _createFloatingButton() {
        const config = this.widget.config;
        const btn = document.createElement('button');
        btn.className = 'accessibility-widget-button';
        btn.setAttribute('aria-label', this.widget.i18n.t('widget.open'));
        btn.setAttribute('title', this.widget.i18n.t('widget.open'));
        btn.setAttribute('type', 'button');

        // Position
        const pos = config.position || 'right';
        btn.classList.add('position-' + pos);

        // Vertical position
        if (config.verticalPosition) {
            btn.style.bottom = config.verticalPosition;
        }

        // Icon (universal accessibility symbol - person with arms out)
        btn.innerHTML = `
            <svg viewBox="0 0 512 512" width="32" height="32" fill="currentColor" aria-hidden="true">
                <circle cx="256" cy="56" r="56"/>
                <path d="M432 112H80c-17.7 0-32 14.3-32 32s14.3 32 32 32h120v48l-72.4 217.2c-5.5 16.5 3.4 34.4 19.9 39.9 16.5 5.5 34.4-3.4 39.9-19.9L224 336h64l36.6 125.2c5.5 16.5 23.4 25.4 39.9 19.9 16.5-5.5 25.4-23.4 19.9-39.9L312 224v-48h120c17.7 0 32-14.3 32-32s-14.3-32-32-32z"/>
            </svg>
        `;

        // Draggable
        if (config.draggable) {
            this._makeDraggable(btn);
        }

        return btn;
    }

    /**
     * Create the main panel
     */
    _createPanel() {
        const panel = document.createElement('div');
        panel.className = 'accessibility-widget-panel';
        panel.classList.add('position-' + (this.widget.config.position || 'right'));
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-label', this.widget.i18n.t('widget.title'));
        panel.setAttribute('aria-hidden', 'true');

        // Header
        panel.appendChild(this._createHeader());

        // Scrollable body
        const body = document.createElement('div');
        body.className = 'accessibility-widget-body';

        // Profiles section
        if (this.widget.config.profiles && this.widget.config.profiles.enabled !== false) {
            body.appendChild(this._createSection('profiles', 'group.profiles'));
        }

        // Combined Navigation + Content section
        const combinedSection = this._createCombinedFeatureSection(
            ['navigation', 'content'],
            'group.navigation_content'
        );
        if (combinedSection) {
            body.appendChild(combinedSection);
        }

        // Text Adjustments (Font Size / Spacing controls)
        body.appendChild(this._createAdjustmentControls());

        // Color and Additional sections
        const groups2 = [
            { key: 'color', label: 'group.color' },
            { key: 'additional', label: 'group.additional' }
        ];

        groups2.forEach(group => {
            const section = this._createFeatureSection(group.key, group.label);
            if (section) {
                body.appendChild(section);
            }
        });

        panel.appendChild(body);

        // Floating font sizing bar (bottom center, EqualWeb style)
        this._floatingBar = this._createFloatingBar();
        this.container.appendChild(this._floatingBar);

        // Footer
        panel.appendChild(this._createFooter());

        return panel;
    }

    /**
     * Create panel header
     */
    _createHeader() {
        const header = document.createElement('div');
        header.className = 'accessibility-widget-header';

        // Title
        const title = document.createElement('h2');
        title.className = 'accessibility-widget-title';
        title.textContent = this.widget.i18n.t('widget.title');
        header.appendChild(title);

        // Header actions
        const actions = document.createElement('div');
        actions.className = 'accessibility-widget-header-actions';

        // Language switcher
        const langSwitcher = this._createLanguageSwitcher();
        actions.appendChild(langSwitcher);

        // Close button
        const closeBtn = document.createElement('button');
        closeBtn.className = 'accessibility-widget-close';
        closeBtn.setAttribute('aria-label', this.widget.i18n.t('widget.close'));
        closeBtn.setAttribute('type', 'button');
        closeBtn.innerHTML = `
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
        `;
        closeBtn.addEventListener('click', () => this.close());
        actions.appendChild(closeBtn);

        header.appendChild(actions);

        return header;
    }

    /**
     * Create language switcher (hidden if only one language available)
     */
    _createLanguageSwitcher() {
        const wrapper = document.createElement('div');
        wrapper.className = 'accessibility-widget-lang-switcher';

        const langs = this.widget.i18n.getAvailableLanguages();

        // Hide switcher if only one language
        if (langs.length <= 1) {
            wrapper.style.display = 'none';
            return wrapper;
        }

        langs.forEach(lang => {
            const btn = document.createElement('button');
            btn.className = 'lang-btn';
            btn.setAttribute('type', 'button');
            btn.textContent = lang.toUpperCase();
            btn.setAttribute('aria-label', lang);
            if (lang === this.widget.i18n.getLanguage()) {
                btn.classList.add('active');
            }
            btn.addEventListener('click', () => {
                this.widget.setLanguage(lang);
            });
            wrapper.appendChild(btn);
        });

        return wrapper;
    }

    /**
     * Create a section for profiles (placeholder, filled by ProfileManager)
     */
    _createSection(key, labelKey) {
        const section = document.createElement('div');
        section.className = 'accessibility-widget-section';
        section.dataset.section = key;

        const heading = document.createElement('h3');
        heading.className = 'accessibility-widget-section-title';
        heading.textContent = this.widget.i18n.t(labelKey);
        section.appendChild(heading);

        const grid = document.createElement('div');
        grid.className = 'accessibility-widget-grid';
        grid.dataset.grid = key;
        section.appendChild(grid);

        return section;
    }

    /**
     * Create combined feature section from multiple groups
     */
    _createCombinedFeatureSection(groupKeys, labelKey) {
        const features = Object.entries(this.widget.features).filter(
            ([, f]) => f.options && groupKeys.includes(f.options.group)
        );

        if (features.length === 0) return null;

        const section = this._createSection(groupKeys[0], labelKey);
        const grid = section.querySelector('.accessibility-widget-grid');

        features.forEach(([key, feature]) => {
            const card = this._createFeatureCard(key, feature);
            grid.appendChild(card);
        });

        return section;
    }

    /**
     * Create feature section with cards
     */
    _createFeatureSection(groupKey, labelKey) {
        const features = Object.entries(this.widget.features).filter(
            ([, f]) => f.options && f.options.group === groupKey
        );

        if (features.length === 0) return null;

        const section = this._createSection(groupKey, labelKey);
        const grid = section.querySelector('.accessibility-widget-grid');

        features.forEach(([key, feature]) => {
            const card = this._createFeatureCard(key, feature);
            grid.appendChild(card);
        });

        return section;
    }

    /**
     * Create a feature card
     */
    _createFeatureCard(key, feature) {
        const card = document.createElement('button');
        card.className = 'accessibility-widget-card';
        card.dataset.feature = key;
        card.setAttribute('type', 'button');
        card.setAttribute('role', 'switch');
        card.setAttribute('aria-checked', 'false');
        card.setAttribute('aria-label', this.widget.i18n.t('feature.' + this._toSnakeCase(key)));

        // Icon - always use default icon map
        const icon = document.createElement('span');
        icon.className = 'card-icon';
        const customIcon = feature.getIcon ? feature.getIcon() : null;
        icon.innerHTML = customIcon || this._getDefaultIcon(key);
        card.appendChild(icon);

        // Label
        const label = document.createElement('span');
        label.className = 'card-label';
        label.textContent = this.widget.i18n.t('feature.' + this._toSnakeCase(key));
        card.appendChild(label);

        // Click handler
        card.addEventListener('click', () => {
            const active = this.widget.toggleFeature(key);
            card.classList.toggle('active', active);
            card.setAttribute('aria-checked', String(active));

            // Show color picker panel for customColors
            if (key === 'customColors' && active) {
                this._showColorPicker(card);
            } else if (key === 'customColors' && !active) {
                this._hideColorPicker();
            }

            // Keep panel scroll position (don't jump to top)
            card.focus({ preventScroll: true });
        });

        // Listen for external toggle
        this.widget.events.on('feature:toggle', (data) => {
            if (data.key === key) {
                card.classList.toggle('active', data.active);
                card.setAttribute('aria-checked', String(data.active));
                if (data.key === 'customColors' && !data.active) {
                    this._hideColorPicker();
                }
            }
        });

        return card;
    }

    /**
     * Create panel footer
     */
    _createFooter() {
        const footer = document.createElement('div');
        footer.className = 'accessibility-widget-footer';

        // Reset button
        const resetBtn = document.createElement('button');
        resetBtn.className = 'accessibility-widget-reset';
        resetBtn.setAttribute('type', 'button');
        resetBtn.textContent = this.widget.i18n.t('widget.reset');
        resetBtn.addEventListener('click', () => {
            if (confirm(this.widget.i18n.t('widget.reset_confirm'))) {
                this.widget.resetAll();
            }
        });
        footer.appendChild(resetBtn);

        // Attribution
        const poweredBy = this.widget.config.poweredBy;
        if (poweredBy && poweredBy.show) {
            const attr = document.createElement('div');
            attr.className = 'accessibility-widget-attribution';
            if (poweredBy.url) {
                attr.innerHTML = `<a href="${poweredBy.url}" target="_blank" rel="noopener">${poweredBy.text || ''}</a>`;
            } else {
                attr.textContent = poweredBy.text || '';
            }
            footer.appendChild(attr);
        }

        return footer;
    }

    /**
     * Create hide widget confirmation modal
     */
    _createHideModal() {
        const modal = document.createElement('div');
        modal.className = 'accessibility-widget-hide-modal';
        modal.setAttribute('role', 'alertdialog');
        modal.setAttribute('aria-hidden', 'true');

        modal.innerHTML = `
            <div class="hide-modal-content">
                <h3>${this.widget.i18n.t('hide.title')}</h3>
                <p>${this.widget.i18n.t('hide.message')}</p>
                <div class="hide-modal-actions">
                    <button type="button" class="hide-modal-confirm">${this.widget.i18n.t('hide.confirm')}</button>
                    <button type="button" class="hide-modal-cancel">${this.widget.i18n.t('hide.cancel')}</button>
                </div>
            </div>
        `;

        modal.querySelector('.hide-modal-confirm').addEventListener('click', () => {
            this._hideWidget();
            this._closeHideModal();
        });
        modal.querySelector('.hide-modal-cancel').addEventListener('click', () => {
            this._closeHideModal();
        });

        return modal;
    }

    /**
     * Create restore button (visible when widget is hidden)
     */
    _createRestoreButton() {
        const btn = document.createElement('button');
        btn.className = 'accessibility-widget-restore';
        btn.setAttribute('type', 'button');
        btn.setAttribute('aria-label', this.widget.i18n.t('hide.restore'));
        btn.style.display = 'none';
        btn.textContent = this.widget.i18n.t('hide.restore');
        btn.addEventListener('click', () => this._restoreWidget());
        return btn;
    }

    /**
     * Open the panel
     */
    open() {
        this.isOpen = true;
        this.panelEl.classList.add('open');
        this.panelEl.setAttribute('aria-hidden', 'false');
        this.button.classList.add('panel-open');

        // Focus trap
        const firstFocusable = this.panelEl.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) firstFocusable.focus();

        this.widget.events.emit('panel:open');
    }

    /**
     * Close the panel
     */
    close() {
        this.isOpen = false;
        this.panelEl.classList.remove('open');
        this.panelEl.setAttribute('aria-hidden', 'true');
        this.button.classList.remove('panel-open');
        this.button.focus();
        this.widget.events.emit('panel:close');
    }

    /**
     * Toggle the panel
     */
    toggle() {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    /**
     * Refresh all panel text (after language change)
     */
    refresh() {
        const wasOpen = this.isOpen;
        this._refreshing = true;

        // Re-create panel content
        if (this.container) {
            this.container.remove();
        }
        this._create();
        this._bindEvents();
        this._checkHiddenState();

        // Re-apply active states
        this.widget.activeFeatures.forEach(key => {
            const card = this.container.querySelector(`[data-feature="${key}"]`);
            if (card) {
                card.classList.add('active');
                card.setAttribute('aria-checked', 'true');
            }
        });

        // Re-render profiles after panel rebuild
        if (this.widget.profileManager && typeof this.widget.profileManager.refresh === 'function') {
            this.widget.profileManager.refresh();
        }

        // Restore open state
        if (wasOpen) {
            this.open();
        }

        // Allow outside click after refresh settles
        requestAnimationFrame(() => { this._refreshing = false; });
    }

    /**
     * Update all feature cards to reflect current state
     */
    updateAllCards() {
        const cards = this.container.querySelectorAll('.accessibility-widget-card[data-feature]');
        cards.forEach(card => {
            const key = card.dataset.feature;
            const active = this.widget.isFeatureActive(key);
            card.classList.toggle('active', active);
            card.setAttribute('aria-checked', String(active));
        });
    }

    /**
     * Bind events
     */
    _bindEvents() {
        this.button.addEventListener('click', () => this.toggle());

        // Close on outside click (only bind once)
        if (!this._outsideClickBound) {
            this._outsideClickBound = true;
            document.addEventListener('click', (e) => {
                if (this._refreshing) return;
                if (this.isOpen && this.container && !this.container.contains(e.target)) {
                    this.close();
                }
            });
        }
    }

    /**
     * Make button draggable
     */
    _makeDraggable(btn) {
        let isDragging = false;
        let startY = 0;
        let startTop = 0;
        let hasMoved = false;

        btn.addEventListener('mousedown', (e) => {
            isDragging = true;
            hasMoved = false;
            startY = e.clientY;
            startTop = btn.offsetTop;
            btn.style.transition = 'none';
        });

        document.addEventListener('mousemove', (e) => {
            if (!isDragging) return;
            const deltaY = e.clientY - startY;
            if (Math.abs(deltaY) > 5) hasMoved = true;
            const newTop = Math.max(10, Math.min(window.innerHeight - 60, startTop + deltaY));
            btn.style.top = newTop + 'px';
        });

        document.addEventListener('mouseup', () => {
            if (isDragging) {
                isDragging = false;
                btn.style.transition = '';
                // Prevent click if dragged
                if (hasMoved) {
                    btn.addEventListener('click', (e) => e.stopImmediatePropagation(), { once: true });
                }
            }
        });
    }

    /**
     * Show hide confirmation modal
     */
    _showHideModal() {
        this.hideModal.classList.add('visible');
        this.hideModal.setAttribute('aria-hidden', 'false');
    }

    /**
     * Close hide confirmation modal
     */
    _closeHideModal() {
        this.hideModal.classList.remove('visible');
        this.hideModal.setAttribute('aria-hidden', 'true');
    }

    /**
     * Hide the widget completely
     */
    _hideWidget() {
        this.close();
        this.button.style.display = 'none';
        this.restoreBtn.style.display = 'block';
        this.widget.storage.set('widget_hidden', true);
    }

    /**
     * Restore hidden widget
     */
    _restoreWidget() {
        this.button.style.display = '';
        this.restoreBtn.style.display = 'none';
        this.widget.storage.remove('widget_hidden');
    }

    /**
     * Check if widget was previously hidden
     */
    _checkHiddenState() {
        if (this.widget.storage.get('widget_hidden')) {
            this.button.style.display = 'none';
            this.restoreBtn.style.display = 'block';
        }
    }

    /**
     * Scroll panel body to top
     */
    scrollToTop() {
        const body = this.panelEl ? this.panelEl.querySelector('.accessibility-widget-body') : null;
        if (body) {
            body.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    /**
     * Create font size / spacing adjustment controls (EqualWeb style)
     */
    _createAdjustmentControls() {
        const section = document.createElement('div');
        section.className = 'accessibility-widget-section';
        section.dataset.section = 'adjustments';

        const heading = document.createElement('h3');
        heading.className = 'accessibility-widget-section-title';
        heading.textContent = this.widget.i18n.t('group.adjustments');
        section.appendChild(heading);

        const fsConfig = this.widget.config.featureSettings?.fontSizing || {};
        const minSize = fsConfig.min || 80;
        const maxSize = fsConfig.max || 200;
        const stepSize = fsConfig.step || 10;

        const controls = [
            { key: 'fontSize', label: 'adjust.font_size', unit: '%', min: minSize, max: maxSize, step: stepSize, initial: 100 },
            { key: 'lineSpacing', label: 'adjust.line_spacing', unit: '', min: 0, max: 10, step: 1, initial: 0 },
            { key: 'letterSpacing', label: 'adjust.letter_spacing', unit: '', min: 0, max: 10, step: 1, initial: 0 },
            { key: 'wordSpacing', label: 'adjust.word_spacing', unit: '', min: 0, max: 10, step: 1, initial: 0 }
        ];

        // Store references to update UI from preset
        const controlRefs = {};

        controls.forEach(ctrl => {
            const row = document.createElement('div');
            row.className = 'dhA11y-adjust-control';

            const label = document.createElement('span');
            label.className = 'adjust-label';
            label.textContent = this.widget.i18n.t(ctrl.label);
            row.appendChild(label);

            const actions = document.createElement('div');
            actions.className = 'adjust-actions';

            const minusBtn = document.createElement('button');
            minusBtn.className = 'adjust-btn';
            minusBtn.setAttribute('type', 'button');
            minusBtn.setAttribute('aria-label', 'Decrease ' + ctrl.key);
            minusBtn.textContent = '−';

            const valueEl = document.createElement('span');
            valueEl.className = 'adjust-value';
            valueEl.textContent = ctrl.initial + (ctrl.unit || '');

            const plusBtn = document.createElement('button');
            plusBtn.className = 'adjust-btn';
            plusBtn.setAttribute('type', 'button');
            plusBtn.setAttribute('aria-label', 'Increase ' + ctrl.key);
            plusBtn.textContent = '+';

            let currentVal = ctrl.initial;

            // Store reference for this control
            controlRefs[ctrl.key] = {
                valueEl: valueEl,
                unit: ctrl.unit || '',
                getValue: () => currentVal,
                setValue: (val) => {
                    currentVal = val;
                    valueEl.textContent = val + (ctrl.unit || '');
                }
            };

            // Restore saved value
            const fontFeature = this.widget.features.fontSizing;
            if (fontFeature) {
                const saved = this.widget.storage.get('font_sizing_values');
                if (saved) {
                    if (ctrl.key === 'fontSize') currentVal = saved.fontSize || ctrl.initial;
                    if (ctrl.key === 'lineSpacing') currentVal = saved.lineSpacing || ctrl.initial;
                    if (ctrl.key === 'letterSpacing') currentVal = saved.letterSpacing || ctrl.initial;
                    if (ctrl.key === 'wordSpacing') currentVal = saved.wordSpacing || ctrl.initial;
                }
                valueEl.textContent = currentVal + (ctrl.unit || '');
            }

            const updateValue = (delta) => {
                const newVal = Math.max(ctrl.min, Math.min(ctrl.max, currentVal + delta));
                if (newVal === currentVal) return;
                currentVal = newVal;
                valueEl.textContent = currentVal + (ctrl.unit || '');

                // Enable font sizing feature if not active
                if (!this.widget.isFeatureActive('fontSizing')) {
                    this.widget.toggleFeature('fontSizing', true);
                }

                const f = this.widget.features.fontSizing;
                if (f) {
                    if (ctrl.key === 'fontSize') {
                        f.currentSize = currentVal;
                        // Apply preset when fontSize changes
                        f._applyPreset(currentVal);
                        
                        if (this.widget.config.debug) {
                            console.log('[A11Y Panel] After preset - lineSpacing:', f.currentLineSpacing, 'letterSpacing:', f.currentLetterSpacing, 'wordSpacing:', f.currentWordSpacing);
                        }
                        
                        // Update UI directly using controlRefs
                        if (controlRefs.lineSpacing) {
                            controlRefs.lineSpacing.setValue(f.currentLineSpacing);
                        }
                        if (controlRefs.letterSpacing) {
                            controlRefs.letterSpacing.setValue(f.currentLetterSpacing);
                        }
                        if (controlRefs.wordSpacing) {
                            controlRefs.wordSpacing.setValue(f.currentWordSpacing);
                        }
                    }
                    if (ctrl.key === 'lineSpacing') { f.currentLineSpacing = currentVal; }
                    if (ctrl.key === 'letterSpacing') { f.currentLetterSpacing = currentVal; }
                    if (ctrl.key === 'wordSpacing') { f.currentWordSpacing = currentVal; }
                    f._apply();
                    f._save();
                }

                // Sync with floating bar
                this.widget.events.emit('fontSizing:change', { key: ctrl.key, value: currentVal });
            };

            minusBtn.addEventListener('click', () => updateValue(-ctrl.step));
            plusBtn.addEventListener('click', () => updateValue(ctrl.step));

            // Listen for reset
            this.widget.events.on('widget:reset', () => {
                currentVal = ctrl.initial;
                valueEl.textContent = currentVal + (ctrl.unit || '');
            });

            // Listen for sync from floating bar and preset updates
            this.widget.events.on('fontSizing:change', (data) => {
                if (data.key === ctrl.key) {
                    currentVal = data.value;
                    valueEl.textContent = currentVal + (ctrl.unit || '');
                }
                
                // When fontSize changes, update spacing controls from preset
                if (data.key === 'fontSize' && ctrl.key !== 'fontSize') {
                    const f = this.widget.features.fontSizing;
                    if (f) {
                        if (ctrl.key === 'lineSpacing') {
                            currentVal = f.currentLineSpacing;
                        } else if (ctrl.key === 'letterSpacing') {
                            currentVal = f.currentLetterSpacing;
                        } else if (ctrl.key === 'wordSpacing') {
                            currentVal = f.currentWordSpacing;
                        }
                        valueEl.textContent = currentVal + (ctrl.unit || '');
                    }
                }
            });

            actions.appendChild(minusBtn);
            actions.appendChild(valueEl);
            actions.appendChild(plusBtn);
            row.appendChild(actions);
            section.appendChild(row);
        });

        return section;
    }

    /**
     * Create floating font sizing bar (bottom center, EqualWeb style)
     * Shows 4 tabs: Font Size | Line Spacing | Word Spacing | Letter Spacing
     * Each tab has a slider + value display
     */
    _createFloatingBar() {
        const bar = document.createElement('div');
        bar.className = 'dhA11y-floating-bar';
        bar.style.display = 'none';

        const fsConfig = this.widget.config.featureSettings?.fontSizing || {};
        const minSize = fsConfig.min || 80;
        const maxSize = fsConfig.max || 200;
        const stepSize = fsConfig.step || 10;

        const tabs = [
            { key: 'fontSize', label: 'adjust.font_size', unit: '%', min: minSize, max: maxSize, step: stepSize, initial: 100 },
            { key: 'lineSpacing', label: 'adjust.line_spacing', unit: '', min: 0, max: 10, step: 1, initial: 0 },
            { key: 'letterSpacing', label: 'adjust.letter_spacing', unit: '', min: 0, max: 10, step: 1, initial: 0 },
            { key: 'wordSpacing', label: 'adjust.word_spacing', unit: '', min: 0, max: 10, step: 1, initial: 0 }
        ];

        // Tab buttons row
        const tabRow = document.createElement('div');
        tabRow.className = 'dhA11y-floating-tabs';

        // Tab content panels
        const contentArea = document.createElement('div');
        contentArea.className = 'dhA11y-floating-content';

        tabs.forEach((tab, idx) => {
            // Tab button
            const tabBtn = document.createElement('button');
            tabBtn.className = 'dhA11y-floating-tab' + (idx === 0 ? ' active' : '');
            tabBtn.setAttribute('type', 'button');
            tabBtn.textContent = this.widget.i18n.t(tab.label);
            tabBtn.dataset.tab = tab.key;
            tabRow.appendChild(tabBtn);

            // Tab content panel
            const panel = document.createElement('div');
            panel.className = 'dhA11y-floating-panel' + (idx === 0 ? ' active' : '');
            panel.dataset.panel = tab.key;

            // Slider row: minus - slider - plus - value
            const sliderRow = document.createElement('div');
            sliderRow.className = 'dhA11y-slider-row';

            const minusBtn = document.createElement('button');
            minusBtn.className = 'dhA11y-slider-btn';
            minusBtn.setAttribute('type', 'button');
            minusBtn.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M19 13H5v-2h14v2z"/></svg>';

            const slider = document.createElement('input');
            slider.type = 'range';
            slider.className = 'dhA11y-slider';
            slider.min = tab.min;
            slider.max = tab.max;
            slider.step = tab.step;
            slider.value = tab.initial;

            const plusBtn = document.createElement('button');
            plusBtn.className = 'dhA11y-slider-btn';
            plusBtn.setAttribute('type', 'button');
            plusBtn.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>';

            const valueDisplay = document.createElement('span');
            valueDisplay.className = 'dhA11y-slider-value';
            valueDisplay.textContent = tab.initial + (tab.unit || '');

            // Restore saved value
            const saved = this.widget.storage.get('font_sizing_values');
            if (saved) {
                const sv = saved[tab.key] || tab.initial;
                slider.value = sv;
                valueDisplay.textContent = sv + (tab.unit || '');
            }

            // Update function
            const applyValue = (val) => {
                val = Math.max(tab.min, Math.min(tab.max, val));
                slider.value = val;
                valueDisplay.textContent = val + (tab.unit || '');

                // Enable font sizing if not active
                if (!this.widget.isFeatureActive('fontSizing')) {
                    this.widget.toggleFeature('fontSizing', true);
                }

                const f = this.widget.features.fontSizing;
                if (f) {
                    if (tab.key === 'fontSize') {
                        f.currentSize = val;
                        // Apply preset when fontSize changes (same as widget Panel)
                        f._applyPreset(val);
                    }
                    if (tab.key === 'lineSpacing') f.currentLineSpacing = val;
                    if (tab.key === 'wordSpacing') f.currentWordSpacing = val;
                    if (tab.key === 'letterSpacing') f.currentLetterSpacing = val;
                    f._apply();
                    f._save();
                }

                // Sync panel adjustment controls
                this.widget.events.emit('fontSizing:change', { key: tab.key, value: val });
            };

            slider.addEventListener('input', () => applyValue(parseInt(slider.value)));
            minusBtn.addEventListener('click', () => applyValue(parseInt(slider.value) - tab.step));
            plusBtn.addEventListener('click', () => applyValue(parseInt(slider.value) + tab.step));

            // Listen for fontSizing:change events to sync slider from Panel or preset
            this.widget.events.on('fontSizing:change', (data) => {
                if (data.key === tab.key) {
                    if (this.widget.config.debug) {
                        console.log('[A11Y FloatingBar] Event received - key:', data.key, 'value:', data.value, 'tab.key:', tab.key);
                    }
                    slider.value = data.value;
                    valueDisplay.textContent = data.value + (tab.unit || '');
                } else if (data.key === 'fontSize' && tab.key !== 'fontSize') {
                    // Update spacing sliders when fontSize changes and preset is applied
                    const f = this.widget.features.fontSizing;
                    if (f) {
                        let val = 0;
                        if (tab.key === 'lineSpacing') val = f.currentLineSpacing;
                        if (tab.key === 'letterSpacing') val = f.currentLetterSpacing;
                        if (tab.key === 'wordSpacing') val = f.currentWordSpacing;
                        slider.value = val;
                        valueDisplay.textContent = val + (tab.unit || '');
                    }
                }
            });

            sliderRow.appendChild(minusBtn);
            sliderRow.appendChild(slider);
            sliderRow.appendChild(plusBtn);
            sliderRow.appendChild(valueDisplay);
            panel.appendChild(sliderRow);
            contentArea.appendChild(panel);

            // Tab click handler
            tabBtn.addEventListener('click', () => {
                tabRow.querySelectorAll('.dhA11y-floating-tab').forEach(t => t.classList.remove('active'));
                contentArea.querySelectorAll('.dhA11y-floating-panel').forEach(p => p.classList.remove('active'));
                tabBtn.classList.add('active');
                panel.classList.add('active');
            });
        });

        // Close button
        const closeBtn = document.createElement('button');
        closeBtn.className = 'dhA11y-floating-close';
        closeBtn.setAttribute('type', 'button');
        closeBtn.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>';
        closeBtn.addEventListener('click', () => this._hideFloatingBar());

        bar.appendChild(tabRow);
        bar.appendChild(contentArea);
        bar.appendChild(closeBtn);

        // Show/hide based on fontSizing feature toggle
        this.widget.events.on('feature:toggle', (data) => {
            if (data.key === 'fontSizing') {
                bar.style.display = data.active ? 'flex' : 'none';
            }
        });

        // Listen for reset
        this.widget.events.on('widget:reset', () => {
            bar.style.display = 'none';
            bar.querySelectorAll('.dhA11y-slider').forEach(s => {
                const panel = s.closest('.dhA11y-floating-panel');
                const key = panel ? panel.dataset.panel : '';
                s.value = key === 'fontSize' ? 100 : 0;
            });
            bar.querySelectorAll('.dhA11y-slider-value').forEach((v, i) => {
                v.textContent = i === 0 ? '100%' : '0';
            });
        });

        // Sync from panel adjustment controls
        this.widget.events.on('fontSizing:change', (data) => {
            const panel = bar.querySelector(`[data-panel="${data.key}"]`);
            if (panel) {
                const slider = panel.querySelector('.dhA11y-slider');
                const valueEl = panel.querySelector('.dhA11y-slider-value');
                if (slider) slider.value = data.value;
                const tab = tabs.find(t => t.key === data.key);
                if (valueEl && tab) valueEl.textContent = data.value + (tab.unit || '');
            }
        });

        return bar;
    }

    /**
     * Hide floating font sizing bar
     */
    _hideFloatingBar() {
        if (this._floatingBar) {
            this._floatingBar.style.display = 'none';
        }
        // Disable fontSizing feature
        if (this.widget.isFeatureActive('fontSizing')) {
            this.widget.toggleFeature('fontSizing', false);
        }
    }

    /**
     * Show color picker panel below the Custom Colors card
     */
    _showColorPicker(card) {
        this._hideColorPicker();
        const panel = document.createElement('div');
        panel.className = 'dhA11y-color-picker-panel';
        panel.dataset.colorPicker = 'true';

        const zones = [
            { key: 'background', label: this.widget.i18n.t('color.background') },
            { key: 'text', label: this.widget.i18n.t('color.text') },
            { key: 'links', label: this.widget.i18n.t('color.links') }
        ];

        zones.forEach(zone => {
            const row = document.createElement('div');
            row.className = 'dhA11y-color-row';

            const lbl = document.createElement('label');
            lbl.textContent = zone.label;
            row.appendChild(lbl);

            const input = document.createElement('input');
            input.type = 'color';
            input.value = '#000000';

            // Restore saved color
            const feature = this.widget.features.customColors;
            if (feature && feature.customValues && feature.customValues[zone.key]) {
                input.value = feature.customValues[zone.key];
            }

            input.addEventListener('input', (e) => {
                const feature = this.widget.features.customColors;
                if (feature) {
                    feature.setColor(zone.key, e.target.value);
                }
            });
            row.appendChild(input);
            panel.appendChild(row);
        });

        // Color presets
        const presets = document.createElement('div');
        presets.className = 'dhA11y-color-presets';
        const presetColors = [
            { bg: '#1a1a2e', text: '#e0e0e0', label: 'Dark Blue' },
            { bg: '#f5f5dc', text: '#333333', label: 'Cream' },
            { bg: '#000000', text: '#00ff00', label: 'Green on Black' },
            { bg: '#fffff0', text: '#000080', label: 'Navy on Ivory' },
            { bg: '#fff8e7', text: '#5b4636', label: 'Sepia' }
        ];

        presetColors.forEach(preset => {
            const btn = document.createElement('button');
            btn.className = 'dhA11y-color-preset';
            btn.setAttribute('type', 'button');
            btn.setAttribute('title', preset.label);
            btn.style.background = `linear-gradient(135deg, ${preset.bg} 50%, ${preset.text} 50%)`;
            btn.addEventListener('click', () => {
                const feature = this.widget.features.customColors;
                if (feature) {
                    feature.setColor('background', preset.bg);
                    feature.setColor('text', preset.text);
                    // Update color inputs
                    const inputs = panel.querySelectorAll('input[type="color"]');
                    if (inputs[0]) inputs[0].value = preset.bg;
                    if (inputs[1]) inputs[1].value = preset.text;
                }
            });
            presets.appendChild(btn);
        });
        panel.appendChild(presets);

        // Insert after the card's parent grid
        const grid = card.parentElement;
        if (grid && grid.parentElement) {
            grid.parentElement.insertBefore(panel, grid.nextSibling);
        }
        this._colorPickerPanel = panel;
    }

    /**
     * Hide color picker panel
     */
    _hideColorPicker() {
        if (this._colorPickerPanel) {
            this._colorPickerPanel.remove();
            this._colorPickerPanel = null;
        }
    }

    /**
     * Convert camelCase to snake_case
     */
    _toSnakeCase(str) {
        return str.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '');
    }

    /**
     * Get default SVG icon for a feature
     */
    _getDefaultIcon(key) {
        const icons = {
            screenReader: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/></svg>',
            keyboardNavigation: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M20 5H4c-1.1 0-1.99.9-1.99 2L2 17c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-9 3h2v2h-2V8zm0 3h2v2h-2v-2zM8 8h2v2H8V8zm0 3h2v2H8v-2zm-1 2H5v-2h2v2zm0-3H5V8h2v2zm9 7H8v-2h8v2zm0-4h-2v-2h2v2zm0-3h-2V8h2v2zm3 3h-2v-2h2v2zm0-3h-2V8h2v2z"/></svg>',
            textReader: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>',
            monochrome: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"/><path d="M12 2v20c5.52 0 10-4.48 10-10S17.52 2 12 2z"/></svg>',
            darkHighContrast: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93s3.05-7.44 7-7.93v15.86z"/></svg>',
            brightHighContrast: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M20 8.69V4h-4.69L12 .69 8.69 4H4v4.69L.69 12 4 15.31V20h4.69L12 23.31 15.31 20H20v-4.69L23.31 12 20 8.69zM12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6zm0-10c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4z"/></svg>',
            lowSaturation: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10 10-4.49 10-10S17.51 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>',
            highSaturation: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10 10-4.49 10-10S17.51 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/><circle cx="12" cy="12" r="5"/></svg>',
            contrastMode: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18V4c4.41 0 8 3.59 8 8s-3.59 8-8 8z"/></svg>',
            customColors: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-1 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8zm-5.5 9c-.83 0-1.5-.67-1.5-1.5S5.67 9 6.5 9 8 9.67 8 10.5 7.33 12 6.5 12zm3-4C8.67 8 8 7.33 8 6.5S8.67 5 9.5 5s1.5.67 1.5 1.5S10.33 8 9.5 8zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 5 14.5 5s1.5.67 1.5 1.5S15.33 8 14.5 8zm3 4c-.83 0-1.5-.67-1.5-1.5S16.67 9 17.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>',
            fontSizing: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M9 4v3h5v12h3V7h5V4H9zm-6 8h3v7h3v-7h3V9H3v3z"/></svg>',
            cursorCustomization: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M13.64 21.97C13.14 22.21 12.54 22 12.31 21.5l-1.9-4.09-2.06 2.06c-.18.18-.44.29-.7.29-.55 0-1-.45-1-1V3.41c0-.89 1.08-1.34 1.71-.71l10.59 10.59c.63.63.18 1.71-.71 1.71h-2.86l1.9 4.09c.24.5.02 1.1-.48 1.34l-2.16 1.04z"/></svg>',
            readingGuide: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M3 13h18v-2H3v2zm0 4h18v-2H3v2zm0-8h18V7H3v2z"/></svg>',
            magnifier: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>',
            readableFont: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M9.93 13.5h4.14L12 7.98 9.93 13.5zM20 2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-4.05 16.5l-1.14-3H9.17l-1.12 3H5.96l5.11-13h1.86l5.11 13h-2.09z"/></svg>',
            imageDescriptions: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>',
            highlightLinks: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>',
            highlightHeaders: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M5 4v3h5.5v12h3V7H19V4H5z"/></svg>',
            highlightElements: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M3 3h18v2H3V3zm0 16h18v2H3v-2zm0-8h18v2H3v-2zm0 4h18v2H3v-2zm0-8h18v2H3V7z"/></svg>',
            enlargeButtons: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M15 3l2.3 2.3-2.89 2.87 1.42 1.42L18.7 6.7 21 9V3h-6zM3 9l2.3-2.3 2.87 2.89 1.42-1.42L6.7 5.3 9 3H3v6zm6 12l-2.3-2.3 2.89-2.87-1.42-1.42L5.3 17.3 3 15v6h6zm12-6l-2.3 2.3-2.87-2.89-1.42 1.42 2.89 2.87L15 21h6v-6z"/></svg>',
            readableMode: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14zM5 15h14v2H5v-2zm0-4h14v2H5v-2zm0-4h14v2H5V7z"/></svg>',
            muteMedia: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>',
            readFocus: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>'
        };
        return icons[key] || '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><circle cx="12" cy="12" r="10"/></svg>';
    }

    /**
     * Destroy panel and clean up
     */
    destroy() {
        if (this.container) {
            this.container.remove();
        }
    }
}
